import React from 'react';
import NavathyMahotsav from './NavathyMahotsav';
import RecentMilestones from './RecentMilestones';

const History = () => {
  return (
    <section id="history" className="section bg-gradient-to-b from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-saffron mb-4">
            Temple History
          </h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            The Sacred Legacy of Sree Krishnaswamy Temple
          </p>
          <div className="decorative-border"></div>
        </div>

        <div className="max-w-6xl mx-auto space-y-12">
          {/* Unique Genesis */}
          <div className="card-temple">
            <h3 className="text-2xl font-bold text-saffron mb-6">
              I. The Unique Genesis
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              The history of the <strong className="font-black text-gray-900">Sree Krishnaswamy Temple</strong> stands as a distinctive chapter 
              in the annals of the <strong className="font-black text-gray-900">Gowda Saraswat Brahmins (GSBs)</strong>. Unlike the revered shrines built by 
              ancestors who <strong className="font-black text-gray-900">migrated with their sacred deities</strong> to preserve their culture, this temple 
              was founded for a profoundly personal, transformative reason: the redemption of a cursed lineage.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              The wealthy <strong className="font-black text-gray-900">Perumbilli Kamath</strong> family, a settled GSB household, was afflicted by Brahmahathya Shapa (Curse for killing a Brahmin), rendering them childless. The family head - Giri Kamath decided to consult an astrologer. Astrological guidance revealed the path to peace: they must consecrate 
              a Vaishnava temple. Driven by this divine directive, the consecration of Lord Vishnu in the form of Sree Rajagopala Krishnaswamy with His consorts, Rukmini and Satyabhama carved from the dark, sacred Krishnashila stone was performed with full Vedic rites by scholars Shri Govinda Bhat and Cherai Shri Madhava Bhat on the auspicious day of Kollavarsham 1024, Edavam 13 (May 24, 1849), on Makayiram star. Following this, the family was blessed with children, confirming the power of divine intervention.
            </p>
            
            {/* Historical Image */}
            <div className="mt-8 text-center">
              <img
                src="https://customer-assets.emergentagent.com/job_krishna-gallery/artifacts/3sf8ei1e_NEW%20COVER%20IMAGE.png"
                alt="Sree Rajagopala Krishnaswamy with His consorts, Rukmini and Satyabhama"
                className="mx-auto max-w-full h-auto rounded-lg shadow-temple lotus-border"
              />
              <p className="text-sm text-gray-600 mt-2">Sree Rajagopala Krishnaswamy with His consorts, Rukmini and Satyabhama</p>
            </div>
          </div>

          {/* Guardian of the Lamp */}
          <div className="card-temple">
            <h3 className="text-2xl font-bold text-saffron mb-6">
              II. The Guardian of the Lamp and Community&apos;s Embrace
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              24 years later, in <strong className="font-black text-gray-900">1048 ME</strong>, the Kamaths handed over the temple to the local GSB 
              community. The community took over governance, but after thirteen years, the meager income strained 
              the committee, forcing them to concede their inability to continue.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              It was here that <strong className="font-black text-gray-900">Sri Janardhana Bhat</strong>, the brother of the main priest, stepped forward, 
              becoming the temple&apos;s unofficial guardian. He accepted the key of the <em>Sree Kovil</em> and, for eight 
              years, sustained the sacred space with his <strong className="font-black text-gray-900">own funds</strong>. Under his care, the <strong className="font-black text-gray-900">Utsava Vigraham</strong> (Processional Deity) in <strong className="font-black text-gray-900">Panchaloha</strong> was consecrated in <strong className="font-black text-gray-900">Kollavarsham 1064</strong>.
            </p>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Realizing the burden on Sri Bhat, the community committee resumed governance in <strong className="font-black text-gray-900">Kollavarsham 1069</strong>, 
              collecting donations to run the temple.
            </p>
          </div>

          {/* Rebirth and Pillars */}
          <div className="card-temple">
            <h3 className="text-2xl font-bold text-saffron mb-6">
              III. Rebirth and the Pillars of Devotion
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              In <strong className="font-black text-gray-900">1093 ME</strong>, destiny called for a grand reconstruction when the top of the{' '}
              <em>Sree Kovil</em> (sanctum sanctorum) was damaged. A new committee headed by <strong className="font-black text-gray-900">Sri P. V. Janardhana Mallan</strong> as the president and <strong className="font-black text-gray-900">Sri S. N. Govinda Prabhu</strong> as the secretary sought the advice of the renowned astrologer, <strong className="font-black text-gray-900">Valiya Kochunni Thampuran of Kodungallur Kovilakam</strong>, who commanded that a mere repair would not suffice. He emphatically decreed that the entire <em>Sree Kovil</em> must be demolished and reconstructed according to the precise <strong className="font-black text-gray-900">Shastraic specifications</strong> to restore the full Divine glory and ensure the welfare of the community.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed">
              The sacred image was temporarily moved to the <em>Balalayam</em> in <strong className="font-black text-gray-900">1095 ME</strong>. 
              The Nidhikalasham for the new temple was placed by our revered Swamiji - <strong className="font-black text-gray-900">HH Shrimath Sukrathindra Thirtha Swamiji</strong> in <strong className="font-black text-gray-900">Kollavarsham 1097</strong>. The work proceeded with the blessings of <strong className="font-black text-gray-900">His Holiness Shrimath Sukrathindra Thirtha Swamiji, Mathadhipathi</strong>{' '}
              of the Sri Kashimath Samsthan, taking six years to complete.
            </p>
            <p className="text-gray-700 mb-6 leading-relaxed">
              The magnificent reconstruction of the <em>Sree Kovil</em> and virtually all the parts of the temple including <strong className="font-black text-gray-900">Nalambalam</strong>, <strong className="font-black text-gray-900">Ganapathy Kovil</strong>, <strong className="font-black text-gray-900">Paakashaala</strong>, <strong className="font-black text-gray-900">Agrashaalas</strong>, <strong className="font-black text-gray-900">Aanapanthal</strong>, <strong className="font-black text-gray-900">Paarshvamandapams</strong>, <strong className="font-black text-gray-900">Namaskara Mandapams</strong> and <strong className="font-black text-gray-900">Gopuram</strong> was completed, and the <em>Punaprathishta</em> (re-consecration) was 
              performed by <strong className="font-black text-gray-900">HH Shrimath Sukrathindra Thirtha Swamiji</strong> in <strong className="font-black text-gray-900">1928 AD</strong>, <strong className="font-black text-gray-900">May 21 (1103 ME), Edavam 13</strong>, a date chosen to align with the original consecration day. The temple 
              has received continuous spiritual support. In <strong className="font-black text-gray-900">1957 AD</strong>, <strong className="font-black text-gray-900">HH Shrimath Sudhindra Thirtha Swamiji, Mathadhipathi of the Samsthan</strong>, chose the premises to observe their first <strong className="font-black text-gray-900">Chaturmasa Vritham</strong> (the four-month vow of monastic life) in the state of Kerala. The <strong className="font-black text-gray-900">Shashtyabdhi Poorthi Mahotsavam</strong> (60th Anniversary) of the re-consecration was celebrated in <strong className="font-black text-gray-900">1988</strong>, in the holy presence of <strong className="font-black text-gray-900">Shrimath Sudhindra Thirtha Swamiji</strong>. Swamiji also gave the community the privilege of organizing their very first <strong className="font-black text-gray-900">Vyasolsav in 1989</strong> on this blessed land.
              
            {/* Spiritual Leaders Image */}
            <div className="mt-8 text-center">
              <img
                src="https://customer-assets.emergentagent.com/job_krishna-gallery/artifacts/lwmqunhs_HH%20SHRIMAD%20SUKRITHINDRA%20TIRTHA%20SWAMIJI_20250928_000252_0000.png"
                alt="HH Shrimath Sukrathindra Thirtha Swamiji and other spiritual leaders"
                className="mx-auto w-full max-w-4xl h-auto rounded-lg shadow-temple lotus-border"
                style={{objectFit: 'contain'}}
              />
            </div>
            </p>
          </div>

          {/* Governance */}
          <div className="card-temple">
            <h3 className="text-2xl font-bold text-saffron mb-6">
              IV. Governance
            </h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Since <strong className="font-black text-gray-900">1937 AD (1113 ME)</strong>, the temple has been managed by a five-member committee, elected from the community for three-year periods, ensuring its continued prosperity and upkeep.
            </p>
            
            {/* New Image above Deval Darshan video */}
            <div className="mt-8 text-center">
              <img
                src="https://customer-assets.emergentagent.com/job_temple-content/artifacts/hfuux8nc_Untitled%20design_20251014_001316_0000%20%281%29.png"
                alt="Temple image"
                className="mx-auto max-w-full h-auto rounded-lg shadow-temple lotus-border"
              />
            </div>

            {/* YouTube Link */}
            <div className="mt-6 text-center">
              <a
                href="https://youtu.be/OG-sS209fwo?si=w6vZtn-o2-e-E7jg"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
              >
                🎥 Deval Darshan Youth of GSB
              </a>
            </div>
            
          </div>

          <NavathyMahotsav />

          <RecentMilestones />

          {/* Attribution */}
          <div className="card-temple mt-12">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-saffron mb-4">Acknowledgment</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                The detailed information about the history of our temple written by the former Thantri of our temple, <strong className="font-black text-gray-900">Brahmashree P. Purushottama Bhat</strong> in Malayalam has been included in the Punaprathishta Navathy Mahotsav 2018 souvenir titled - <strong className="font-black text-gray-900">&apos;Shri Krishnarpanam&apos;</strong>. Key points have been translated and added in the history section.
              </p>
              <p className="text-gray-700 leading-relaxed">
                There are points in the history section which are taken from the contributions of the devotees of the Lord on <strong className="font-black text-gray-900">Blogspot</strong> and <strong className="font-black text-gray-900">Holyprasadam</strong> in English. We express our heartfelt gratitude to all the dedicated devotees for their valuable research and documentation of our temple&apos;s rich heritage.
              </p>
              
              {/* Acknowledgment Image */}
              <div className="mt-8 text-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_krishna-temple/artifacts/9v5c480n_Untitled%20design_20250930_174002_0000.png"
                  alt="HH Shrimath Sudhindra Thirtha Swamiji during the First Vyasolsav at our temple in 1989"
                  className="mx-auto max-w-full h-auto rounded-lg shadow-temple lotus-border"
                />
                <p className="text-sm text-gray-600 mt-3">
                  <strong className="font-black text-gray-900">HH Shrimath Sudhindra Thirtha Swamiji</strong> during the First Vyasolsav at our temple in <strong className="font-black text-gray-900">1989</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default History;